// Admin → Announcements. Send an in-app + push notification to a user segment
// (role + optional country). docs/replan-v2.md (admin console, slice 4).

import { Alert, Button, Input } from '@fajrarabi/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { sendAnnouncement, type AnnouncementInput } from '~/lib/admin';
import { ApiError } from '~/lib/api';

export const Route = createFileRoute('/_authed/announcements')({
  component: AnnouncementsPage,
});

const ROLES: AnnouncementInput['role'][] = [
  'all',
  'parent',
  'teacher',
  'school_admin',
  'kid',
  'student',
];

function AnnouncementsPage() {
  const [role, setRole] = useState<AnnouncementInput['role']>('parent');
  const [country, setCountry] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  const send = useMutation({
    mutationFn: () =>
      sendAnnouncement({
        role,
        country: country.trim() ? country.trim().toUpperCase() : null,
        title: title.trim(),
        body: body.trim(),
        link: link.trim() || null,
      }),
    onSuccess: (r) => {
      setSent(r.recipients);
      setTitle('');
      setBody('');
      setLink('');
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Something went wrong.'),
  });

  const canSend = title.trim() !== '' && body.trim() !== '';

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-semibold text-admin-900">Announcements</h1>
      <p className="mt-1 text-sm text-ink-700">
        Send an in-app + push notification to a segment of users.
      </p>

      {sent !== null && (
        <Alert tone="success" className="mt-4">
          Sent to {sent} {sent === 1 ? 'user' : 'users'}.
        </Alert>
      )}
      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
        </Alert>
      )}

      <form
        className="mt-6 space-y-4 rounded-2xl border border-burgundy-500/10 bg-white p-5"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setSent(null);
          if (canSend) send.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-medium text-ink-700">
            Audience
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AnnouncementInput['role'])}
              className="mt-1 h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r === 'all' ? 'All users' : r}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-ink-700">
            Country (optional, ISO-2)
            <Input
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              placeholder="QA"
              maxLength={2}
            />
          </label>
        </div>

        <label className="block text-sm font-medium text-ink-700">
          Title
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
        </label>
        <label className="block text-sm font-medium text-ink-700">
          Message
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={500}
            className="mt-1 w-full rounded-xl border border-ink-500/20 bg-white px-3 py-2 text-ink-900"
          />
        </label>
        <label className="block text-sm font-medium text-ink-700">
          Deep link (optional, e.g. /billing)
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="/billing" />
        </label>

        <Button type="submit" disabled={!canSend || send.isPending}>
          {send.isPending ? 'Sending…' : 'Send announcement'}
        </Button>
      </form>
    </div>
  );
}
